import * as React from "react";
import { I18n } from "react-redux-i18n";
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
} from "@common/components/modal";

import { getTestAttributes } from "@common/utils/tests";
import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import { FolderCreating } from "@common/components/FormatManagement/components/folder-creating";
import { FormatFieldSelector } from "@common/components/format-field-selector";

import { QueryManagementProps } from "./query-management.types";
import { useQueryManagementController } from "./query-management.controller";
import { FormControl } from "../form-control";

import "./query-management.styles.scss";

export const QueryManagement: React.FC<QueryManagementProps> = (props) => {
  const {
    onClose,
    withSearch,
    onSubmit,
    getId,
    submitText,
    moduleName,
    submitBtnDisabled,
    formatNameSearchPlaceholder,
    formatDescriptionSearchPlaceholder,
  } = props;

  const {
    folders,
    formats,
    isFoldersLoading,
    isFormatsLoading,
    selectedFolder,
    searchKeyword,
    selectedFormat,
    formatFolderCreatingDialogOpened,
    handleSearchChange,
    handleSelectedFolderChange,
    handleSelectedFormatChange,
    openFormatFolderCreatingDialog,
    closeFormatFolderCreatingDialog,
    handleFormatFolderCreatingSubmit,
    handleFormatFolderDelete,
    handleFormatDelete,
    isFormatIdEmpty,
    isFolderIdEmpty,
    handleChangeFormatDescription,
  } = useQueryManagementController(props);

  return (
    <Modal
      show
      bsSize="lg"
      bsClass="QueryManagement bootstrap modal"
      onHide={onClose}
      onEscapeKeyDown={onClose}
      animation={false}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle id={getId?.modalTitle}>{I18n.t("Query Format")}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        {formatFolderCreatingDialogOpened && (
          <FolderCreating
            getId={{
              folderName: getId?.creatingFolderName,
              folder: getId?.creatingFolderItem,
              collapseIcon: getId?.creatingFolderCollapseItem,
              cancelBtn: getId?.creatingFolderCancelBtn,
              createBtn: getId?.creatingFolderCreateBtn,
            }}
            close={closeFormatFolderCreatingDialog}
            folders={folders}
            onSubmit={handleFormatFolderCreatingSubmit}
          />
        )}
        <Flex>
          <FormatFieldSelector
            moduleName={moduleName}
            withSearch={withSearch}
            folders={folders}
            isFoldersLoading={isFoldersLoading}
            onSelectedFolderChange={handleSelectedFolderChange}
            formats={formats}
            isFormatsLoading={isFormatsLoading}
            onSelectedFormatChange={handleSelectedFormatChange}
            searchKeyword={searchKeyword}
            onSearchKeyword={handleSearchChange}
            selectedFolder={selectedFolder}
            selectedFormat={selectedFormat}
            className="QueryManagement__format-field-selector"
            classes={{
              searchForm:
                "QueryManagement__format-field-selector__search__form",
              search: "QueryManagement__format-field-selector__search",
            }}
            getId={{
              searchIcon: getId?.searchIcon,
              search: getId?.search,
              folder: getId?.folder,
            }}
            searchPlaceholder={formatNameSearchPlaceholder}
            headerComponent={
              <FormControl
                placeholder={formatDescriptionSearchPlaceholder}
                className="QueryManagement__format-field-selector__format-desc"
                value={selectedFormat ? selectedFormat.description : ""}
                onChange={handleChangeFormatDescription}
              />
            }
          />
          <div className="QueryManagement__actions">
            <Button
              {...getTestAttributes({ id: "btn-ss-format-save-load" })}
              className="QueryManagement__btn"
              disabled={
                submitBtnDisabled !== undefined
                  ? submitBtnDisabled
                  : isFormatIdEmpty
              }
              onClick={() => {
                onSubmit({
                  searchKeyword,
                  folder: selectedFolder,
                  format: selectedFormat!,
                });
              }}
            >
              {submitText || I18n.t("Load")}
            </Button>
            <Button
              {...getTestAttributes({ id: "btn-ss-format-new-folder" })}
              className="QueryManagement__btn"
              onClick={openFormatFolderCreatingDialog}
            >
              {I18n.t("New Folder")}
            </Button>
            <Button
              {...getTestAttributes({ id: "btn-ss-format-delete-folder" })}
              className="QueryManagement__btn"
              disabled={isFolderIdEmpty}
              onClick={handleFormatFolderDelete}
            >
              {I18n.t("Delete Folder")}
            </Button>
            <Button
              {...getTestAttributes({ id: "btn-ss-format-delete-format" })}
              className="QueryManagement__btn"
              disabled={isFormatIdEmpty}
              onClick={handleFormatDelete}
            >
              {I18n.t("Delete Format")}
            </Button>
          </div>
        </Flex>
      </ModalBody>
    </Modal>
  );
};
